import { DrawCustomModeThis } from "@mapbox/mapbox-gl-draw";
import { Feature, GeoJsonProperties, LineString, Point, Position } from "geojson";
import rbush, { RBush } from "geojson-rbush";
import minBy from "lodash/minBy";
import omit from "lodash/omit";
import sortBy from "lodash/sortBy";
import mapboxgl from "mapbox-gl";

import { findNamedShortestPath, PathGraph } from "fond/draw/modes/base/pathGraph";
import * as turf from "fond/turf";
import { Architecture, LayerIds, Maybe } from "fond/types";
import { isPoint } from "fond/types/geojson";
import { layerPalette } from "fond/utils/colors";

import {
  AssignmentCableFeature,
  AssignmentCablePreviewFeature,
  AssignmentCableSystemFeature,
  CableFeature,
  FeatureTypeMap,
  ServiceLocationFeature,
  ServiceLocationProperties,
  SinkGroupFeature,
  SinkGroupProperties,
  SourceFeature,
  SourceProperties,
} from "./types";

/**
 * Filters and returns only DROP cables from the fibre cable layer.
 *
 * Mutates `groups` by increasing the fibre count for each cable associated with a given group.
 */
export const getCables = (cables: Feature<LineString>[], sourceLookup: Map<string, SourceFeature>, groups: SinkGroupFeature[]): CableFeature[] => {
  const sinkGroupLookup = new Map<string, SinkGroupFeature>(groups.flatMap((g) => g.properties.serviceLocationIds.map((id) => [id, g])));

  return cables
    .map((f: Feature) => {
      if (f.properties?.Tier !== "DROP" || typeof f.properties?.FondID !== "string" || typeof f.properties?.EndID !== "string") {
        return null;
      }

      const source = sourceLookup.get(f.properties?.StartID);
      if (!source) return null;

      const group = sinkGroupLookup.get(f.properties.EndID);
      if (!group) return null;

      const fibreCount = typeof f.properties?.Size === "number" ? f.properties.Size : 1;
      group.properties.fibreCount += fibreCount;

      return turf.feature(
        f.geometry,
        {
          type: "cable",
          source: omit(source, "remainingCapacity"),
          sinkGroup: group,
          fondId: f.properties.FondID,
        },
        { id: f.id }
      );
    })
    .filter((f) => f !== null);
};

/**
 * Return the square of the Euclidean distance between points f and g.
 *
 * Should not be used in contexts where the distance should be 'correct' - this metric does not account
 * for the spherical nature of the Earth.
 *
 * @param f A point feature
 * @param g A point feature
 */
const euclideanDistanceSquared = (f: Feature<Point>, g: Feature<Point>): number => {
  const dx = Math.abs(f.geometry.coordinates[0] - g.geometry.coordinates[0]);
  const dy = Math.abs(f.geometry.coordinates[1] - g.geometry.coordinates[1]);
  return dx * dx + dy * dy;
};

/**
 * Helper function to find a feature at a specific coordinate using spatial search.
 * @param tree The rbush spatial tree to search
 * @param position The coordinate to search for [lng, lat]
 * @param tolerance Distance tolerance for matching (default: 0.0000001 degrees)
 * @returns The matching feature or undefined
 */
function findPointAtCoordinate<P extends GeoJsonProperties>(
  tree: RBush<Point, P>,
  position: [number, number],
  tolerance = 0.0000001
): Feature<Point, P> | undefined {
  const searchPoint = turf.point(position);
  const results = tree.search(turf.buffer(searchPoint, tolerance));
  if (results.features.length === 0) return undefined;
  return minBy(results.features, (f) => euclideanDistanceSquared(f, searchPoint));
}

/**
 * Convert raw service location features to grouped features, detecting collocation.
 *
 * We impose the following limitation: all service locations that are spatially colocated
 * must be served from the same source location. This may end up creating multiple sources
 * (if there are a lot of demands), but it avoids the complexity of trying to serve a
 * single coordinate from multiple disjoint sources.
 */
export const getSinkGroups = (
  rawServiceLocations: Feature<Point>[],
  rawCables: Feature<LineString>[],
  sourceLookup: Map<string, SourceFeature>
): SinkGroupFeature[] => {
  const serviceLocations = rawServiceLocations
    .map((f): ServiceLocationFeature | null => {
      if (typeof f.properties?.FondID !== "string") return null;
      return turf.point(f.geometry.coordinates, { type: "serviceLocation", fondId: f.properties.FondID });
    })
    .filter((f) => f !== null);
  if (serviceLocations.length === 0) return [];

  // Identify colocated sources
  const sourceTree = rbush<Point, SourceProperties>();
  sourceTree.load(Array.from(sourceLookup.values()));

  const sourceGroupKeyLookup = new Map<string, number>();

  sourceLookup.forEach((source) => {
    if (sourceGroupKeyLookup.has(source.properties.fondId)) {
      return;
    }
    const sourceGroupKey = sourceGroupKeyLookup.size + 1;
    const nearbySources = sourceTree.search(source).features;
    nearbySources.forEach((f) => sourceGroupKeyLookup.set(f.properties.fondId, sourceGroupKey));
  });

  // Sink -> source mapping
  const sinkToSource = new Map();
  rawCables.forEach((cable) => {
    if (typeof cable.properties?.StartID !== "string" || typeof cable.properties?.EndID !== "string" || cable.properties?.Tier !== "DROP") {
      return;
    }
    const source = sourceGroupKeyLookup.get(cable.properties.StartID);
    if (!source) {
      return;
    }
    const sink = cable.properties.EndID;
    sinkToSource.set(sink, source);
  });

  // Group collocated service locations with collocated sources
  const groups: SinkGroupFeature[] = [];
  const processed = new Set<string>();

  // Build spatial index for efficient proximity queries
  const tree = rbush<Point, ServiceLocationProperties>();
  tree.load(serviceLocations);

  serviceLocations.forEach((sl, index) => {
    if (processed.has(sl.properties.fondId)) return;

    const sourceGroupKey = sinkToSource.get(sl.properties.fondId) ?? `unique-${index}`;

    const nearby = tree.search(sl);
    const groupMembers = nearby.features
      .filter((sink) => sink.properties.fondId === sl?.properties.fondId || sinkToSource.get(sink.properties.fondId) === sourceGroupKey)
      .map((f) => f.properties.fondId);
    groupMembers.forEach((id) => processed.add(id));

    // Create group feature at the representative coordinate
    groups.push(
      turf.feature(sl.geometry, {
        type: "sinkGroup" as const,
        groupId: `sinkGroup-${index}`,
        serviceLocationIds: groupMembers,
        // This will be populated later based on cable features
        fibreCount: 0,
      })
    );
  });

  return groups;
};

export const getEdgeFibreCount = (feature: CableFeature | AssignmentCableFeature): number => {
  return getOriginalCable(feature).properties.sinkGroup.properties.fibreCount;
};

export const getEdgeSinkGroupId = (feature: CableFeature | AssignmentCableFeature): string => {
  return getOriginalCable(feature).properties.sinkGroup.properties.groupId;
};

export const getExistingAssignments = (
  assignments: Feature<LineString>[],
  sinkGroups: SinkGroupFeature[],
  sources: SourceFeature[]
): Map<string, { source: SourceFeature; geometry: LineString }> => {
  const existingAssignments = new Map();
  if (assignments.length === 0) return existingAssignments;

  // We need to resolve the geometry of an assignment to the (sink group, source) pair. We rely on a pair of
  // spatial indexes to make these lookups efficiently.
  const groupTree = rbush<Point, SinkGroupProperties>();
  groupTree.load(sinkGroups);

  const sourceTree = rbush<Point, SourceProperties>();
  sourceTree.load(sources);

  assignments.forEach((assignment) => {
    if (assignment.geometry.coordinates.length <= 1) return;

    // When assignments are uploaded we attach a 'type' property that is either 'user_input' or 'system_input'. The
    // system_input assignments are required for technical reasons that aren't worth going into here, but we
    // don't want to show them to the end-user.
    if (assignment.properties?.type !== "user_input") return;

    const maybeSourcePosition = assignment.geometry.coordinates[0] as [number, number];
    const source = findPointAtCoordinate(sourceTree, maybeSourcePosition);
    if (!source) return;

    const maybeServiceLocationPosition = assignment.geometry.coordinates.at(-1) as [number, number];
    const group = findPointAtCoordinate(groupTree, maybeServiceLocationPosition);
    if (!group) return;

    existingAssignments.set(group.properties.groupId, { source, geometry: assignment.geometry });
  });

  return existingAssignments;
};

/**
 * Do an approximate spatial sort on the given point features, returning a new array
 * where spatially close features are more likely to be close together in the output.
 *
 * @param points An array of point features.
 */
function sortPointsSpatially<T extends GeoJsonProperties>(points: Feature<Point, T>[]): Feature<Point, T>[] {
  if (points.length === 0) return [];

  // The sort works like this: pick a point in the 'corner' of the dataset, then sort all other points
  // based on their Euclidean distance to that point. This is rough, but simple.
  const minPoint = sortBy(points, [(f) => f.geometry.coordinates[0], (f) => f.geometry.coordinates[1]])[0];
  if (!minPoint) {
    // This is impossible
    throw new Error("Failed to find a minimal point");
  }

  return sortBy(points, (feature) => euclideanDistanceSquared(feature, minPoint));
}

/**
 * Filters and returns only DROP hubs from the hub layer.
 *
 * @param hubs Raw hub features from the hub layer
 * @param cables Cable features (cables that haven't been reassigned)
 * @param assignments Assignment cable features (cables that have been reassigned)
 * @param sourceMaxCableCount Maximum capacity per source from architecture
 * @returns Source features with calculated remaining capacity
 */
export const getSources = (
  hubs: Feature<Point>[],
  cables: CableFeature[],
  assignments: AssignmentCableFeature[],
  sourceMaxCableCount: number | null
): SourceFeature[] => {
  const sources = hubs.filter(
    (f: Feature): f is Feature<Point, { FondID: string }> =>
      f.properties?.Tier === "DROP" && f.properties?.Type === "HUB" && typeof f.properties?.FondID === "string" && isPoint(f)
  );

  // Count how many cables are currently assigned to each source
  const cableCountBySource = new Map<string, number>();

  if (sourceMaxCableCount !== null) {
    // Count cables that haven't been reassigned
    cables.forEach((cable) => {
      const sourceId = cable.properties.source.properties.fondId;
      const count = getEdgeFibreCount(cable);
      cableCountBySource.set(sourceId, (cableCountBySource.get(sourceId) || 0) + count);
    });

    // Count assignments (reassigned cables)
    assignments.forEach((assignment) => {
      const sourceId = assignment.properties.source.properties.fondId;
      const count = getEdgeFibreCount(assignment);
      cableCountBySource.set(sourceId, (cableCountBySource.get(sourceId) || 0) + count);
    });
  }

  return sortPointsSpatially(sources).map((f, i) => {
    const usedCapacity = cableCountBySource.get(f.properties.FondID) || 0;
    const remainingCapacity = sourceMaxCableCount != null ? sourceMaxCableCount - usedCapacity : null;

    return turf.feature(
      f.geometry,
      {
        type: "source",
        fondId: f.properties.FondID,
        color: layerPalette[i % layerPalette.length],
        remainingCapacity,
      },
      { id: f.id }
    );
  });
};

export const createAssignmentCable = (
  feature: CableFeature | AssignmentCableFeature,
  source: SourceFeature,
  graph?: PathGraph
): AssignmentCableFeature | CableFeature => {
  const originalCable = getOriginalCable(feature);

  // Use the cable if it is already going to where we want.
  if (cableStartsAtSource(originalCable, source)) {
    return originalCable;
  }

  return turf.lineString(
    createAssignmentCoordinates({ originalCable, source, graph }),
    {
      type: "assignmentCable" as const,
      originalCable,
      source: omit(source, "remainingCapacity"),
      layerId: LayerIds.dropAssignment,
    },
    {
      id: `${originalCable.id}-modified`,
    }
  );
};

export const isAssignmentCable = (feature: AssignmentCableSystemFeature): feature is AssignmentCableFeature =>
  feature.properties.type === "assignmentCable";
const isCable = (feature: AssignmentCableSystemFeature): feature is CableFeature => feature.properties.type === "cable";
const cableStartsAtSource = (cable: CableFeature, source: SourceFeature): boolean =>
  cable.properties.source.properties.fondId === source.properties.fondId;

export const getOriginalCable = (feature: CableFeature | AssignmentCableFeature): CableFeature => {
  if (isCable(feature)) {
    return feature;
  }
  if (isAssignmentCable(feature)) {
    return feature.properties.originalCable;
  }
  throw new Error("Couldn't get original cable");
};

interface AssignmentCoordinateArgs {
  originalCable: CableFeature;
  source?: Maybe<SourceFeature>;
  mousePosition?: Maybe<number[]>;
  graph?: Maybe<PathGraph>;
}

export const createAssignmentCoordinates = ({ originalCable, source, mousePosition, graph }: AssignmentCoordinateArgs): Position[] => {
  // It's possible the user could create an assignment to another source, and then move that assignment
  // back to its original source. If that happens then we return the original cable instead of creating
  // a straight-line assignment.
  if (source && cableStartsAtSource(originalCable, source)) {
    return originalCable.geometry.coordinates;
  }

  if (graph && source) {
    const coords = findNamedShortestPath(
      graph,
      `dropHub:${source.properties.fondId}`,
      `slg:${originalCable.properties.sinkGroup.properties.groupId}`
    );
    if (coords) {
      return coords;
    }
  }

  const serviceLocationPosition = originalCable.geometry.coordinates.at(-1);
  if (!serviceLocationPosition) {
    // if a user clicks on a cable without a geometry, does it make an error?
    throw new Error("Cannot create preview line based on empty cable");
  }

  let startPosition;
  if (source) {
    startPosition = source.geometry.coordinates;
  } else if (mousePosition) {
    startPosition = mousePosition;
  } else {
    startPosition = serviceLocationPosition;
  }

  return [startPosition, serviceLocationPosition];
};

export const PREVIEW_LINE_ID = "assignment-preview-line";

export const createPreviewLine = (
  feature: CableFeature | AssignmentCableFeature,
  source?: SourceFeature | null,
  mousePosition?: number[]
): AssignmentCablePreviewFeature => {
  const originalCable = getOriginalCable(feature);
  return turf.lineString(
    createAssignmentCoordinates({ originalCable, source, mousePosition }),
    {
      type: "assignmentCablePreview" as const,
      originalCable,
      source: source ? omit(source, "remainingCapacity") : null,
    },
    {
      id: PREVIEW_LINE_ID,
    }
  );
};

/**
 * Get all features of the specified types at a point.
 * Returns an array of all matching features within the buffer radius.
 */
export const getFeaturesAtPoint = <T extends readonly (keyof FeatureTypeMap)[]>(
  draw: DrawCustomModeThis,
  point: mapboxgl.Point,
  bufferPixels: number,
  types: T
): FeatureTypeMap[T[number]][] => {
  const queryBbox: [mapboxgl.PointLike, mapboxgl.PointLike] = [
    [point.x - bufferPixels, point.y - bufferPixels],
    [point.x + bufferPixels, point.y + bufferPixels],
  ];
  const featuresAtPoint = draw.featuresAt(null, queryBbox, "click");

  const matchingFeatures: FeatureTypeMap[T[number]][] = [];

  for (const rawFeature of featuresAtPoint) {
    if (types.includes(rawFeature.properties?.user_type) && rawFeature.properties?.id) {
      // mapbox draw isn't generic over the property type, so a cast is required.
      const drawFeature = draw.getFeature(rawFeature.properties.id);
      const geojson = drawFeature.toGeoJSON();
      matchingFeatures.push(geojson as FeatureTypeMap[T[number]]);
    }
  }

  return matchingFeatures;
};

/**
 * Get the first feature of the specified types at a point.
 * Returns undefined if no matching feature is found.
 */
export const getFeatureAtPoint = <T extends readonly (keyof FeatureTypeMap)[]>(
  draw: DrawCustomModeThis,
  point: mapboxgl.Point,
  bufferPixels: number,
  types: T
): FeatureTypeMap[T[number]] | undefined => {
  return getFeaturesAtPoint(draw, point, bufferPixels, types)[0];
};

/**
 * Calculate the maximum number of cables that can be assigned to a single source.
 */
export const calculateSourceMaxCableCount = (architecture: Maybe<Architecture>): number | null => {
  if (!architecture?.Tier1?.Hubs?.Sizes) {
    return null;
  }

  const maxPortCount = Math.max(...architecture.Tier1.Hubs.Sizes);
  const spare = architecture.Tier1.Hubs?.Spare;

  let sparePorts: number;
  if (spare?.AbsoluteValue != null) {
    sparePorts = spare.AbsoluteValue;
  } else if (spare?.PercentValue != null) {
    sparePorts = Math.ceil(maxPortCount * (spare.PercentValue / 100));
  } else {
    sparePorts = 0;
  }

  return maxPortCount - sparePorts;
};
